import { Subscribable } from "@linqx/core";
import type { AuthSession, AuthStorage } from "./types";

const SESSION_KEY = "LINQX::session";

class SessionManagerInternal extends Subscribable {
  /** @deprecated Use `session` instead */
  public accessToken: AuthSession | undefined;
  private initialized = false;
  private storage: AuthStorage | undefined;

  public get session(): AuthSession | undefined {
    return this.accessToken;
  }

  public set session(value: AuthSession | undefined) {
    this.accessToken = value;
  }

  public setStorage = (storage: AuthStorage): void => {
    this.storage = storage;
  };

  public start = async (): Promise<void> => {
    if (!this.initialized) {
      await this.loadFromStorage();
      this.initialized = true;
      this.trigger(this.accessToken);
    }
  };

  public getAccessToken = (): string | undefined => {
    return this.accessToken?.accessToken;
  };

  /** @deprecated Use `setSession` instead */
  public setToken = async (session: AuthSession): Promise<boolean> => {
    return this.setSession(session);
  };

  public setSession = async (session: AuthSession): Promise<boolean> => {
    await this.start();
    this.accessToken = session;
    try {
      await this.storage?.setItem(
        SESSION_KEY,
        JSON.stringify(this.accessToken),
      );
    } catch {
      // Silently fail storage errors
    }
    this.trigger(this.accessToken);
    return true;
  };

  /** @deprecated Use `clearSession` instead */
  public clearToken = async (): Promise<boolean> => {
    return this.clearSession();
  };

  public clearSession = async (): Promise<boolean> => {
    await this.start();
    this.accessToken = undefined;
    await this.clearStorage();
    this.trigger(this.accessToken);
    return true;
  };

  public isAuthenticated = (): boolean => {
    return !!this.accessToken?.accessToken;
  };

  private loadFromStorage = async (): Promise<AuthSession | null> => {
    try {
      const stored = await this.storage?.getItem(SESSION_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as AuthSession;
        if (parsed?.accessToken) {
          this.accessToken = parsed;
          return this.accessToken;
        }
      }
      this.accessToken = undefined;
      return null;
    } catch {
      this.accessToken = undefined;
      return null;
    }
  };

  private clearStorage = async (): Promise<boolean> => {
    try {
      await this.storage?.removeItem(SESSION_KEY);
      return true;
    } catch {
      return false;
    }
  };
}

export const SessionManager = new SessionManagerInternal();
